<template>
  <div v-if="stacked" class="note-pairs">
    <div v-for="(row, r) in pairRows" :key="r" class="note-pairs__row">
      <div class="note-pairs__main">
        <span class="note-pairs__label" :class="{ 'note-pairs__label--group': row.subs.length }">{{ row.label }}</span>
        <span
          v-if="row.headValue"
          class="note-pairs__value"
          :class="{ 'note-pairs__value--free': !row.label && !row.subs.length }">
          {{ row.headValue }}
        </span>
      </div>
      <div v-for="(sub, p) in row.subs" :key="p" class="note-pairs__sub">
        <span class="note-pairs__label">{{ sub.label }}</span>
        <span class="note-pairs__value">{{ sub.value }}</span>
      </div>
    </div>
  </div>
  <div
    v-else
    ref="fitWrap"
    class="note-table-fit"
    :class="{ 'note-table-fit--fit': fit }"
    :style="fit ? { height: fitHeight } : undefined">
    <table
      ref="tableEl"
      class="note-table"
      :class="{ 'note-table--plain': !rich }"
      :style="{ fontSize: `${fontSize}px`, transform: fit && scale < 1 ? `scale(${scale})` : undefined }">
      <tbody>
        <template v-if="rich">
          <tr v-for="(row, r) in rows" :key="r">
            <td
              v-for="(cell, c) in row"
              :key="c"
              :colspan="cell.c || undefined"
              :rowspan="cell.r || undefined"
              :class="{
                'note-table__cell--label': cell.b,
                'note-table__cell--center': cell.a === 'c',
                'note-table__cell--right': cell.a === 'r',
              }">
              {{ cell.t }}
            </td>
          </tr>
        </template>
        <template v-else>
          <tr v-for="(row, r) in rows" :key="r">
            <td v-for="(cell, c) in row" :key="c">{{ cell }}</td>
          </tr>
        </template>
      </tbody>
    </table>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";

const props = defineProps({
  rows: {
    type: Array,
    required: true,
  },
  rich: {
    type: Boolean,
    default: false,
  },
  fontSize: {
    type: Number,
    default: 13,
  },
  stacked: {
    type: Boolean,
    default: false,
  },
  fit: {
    type: Boolean,
    default: false,
  },
});

const cellText = (cell) => (cell !== null && typeof cell === "object" ? cell.t : String(cell ?? ""));

// fit: scale the whole table down to the container width so phones see the
// entire sheet as a preview; the viewer shows it full size.
const fitWrap = ref(null);
const tableEl = ref(null);
const scale = ref(1);
const fitHeight = ref("auto");
let resizeObserver;

function measureFit() {
  if (!props.fit || !fitWrap.value || !tableEl.value) return;
  const natural = tableEl.value.offsetWidth;
  const available = fitWrap.value.clientWidth;
  scale.value = natural > available && available > 0 ? available / natural : 1;
  fitHeight.value = `${Math.ceil(tableEl.value.offsetHeight * scale.value)}px`;
}

onMounted(() => {
  measureFit();
  if (typeof ResizeObserver !== "undefined" && fitWrap.value) {
    resizeObserver = new ResizeObserver(measureFit);
    resizeObserver.observe(fitWrap.value);
    if (tableEl.value) resizeObserver.observe(tableEl.value);
  }
  document.fonts?.ready?.then(measureFit);
});

onBeforeUnmount(() => resizeObserver?.disconnect());

watch(
  () => [props.fit, props.rows, props.fontSize],
  () => nextTick(measureFit)
);

/**
 * Reshape grid rows into label/value pairs for the stacked mobile list:
 * cell 1 is the row label; remaining non-empty cells chunk into sub-pairs
 * (e.g. "Authorization Issued by | Drug | OPTUM RX | Nursing | 0").
 */
const pairRows = computed(() =>
  props.rows
    .map((row) => {
      const cells = row.map(cellText).filter((text) => text);
      if (!cells.length) return null;
      const [label, ...rest] = cells;
      if (!rest.length) return { label: "", headValue: label, subs: [] };
      let items = rest;
      let headValue = "";
      if (rest.length % 2 === 1) {
        headValue = rest[0];
        items = rest.slice(1);
      }
      const subs = [];
      for (let i = 0; i < items.length; i += 2) {
        subs.push({ label: items[i], value: items[i + 1] ?? "" });
      }
      return { label, headValue, subs };
    })
    .filter(Boolean)
);
</script>

<style lang="scss" scoped>
@import "@/styles/_variables.scss";

// ── Stacked label/value list (mobile) ────────────────────────────────────
.note-pairs {
  font-family: $lh_font_family_dm_sans;
}

.note-pairs__row {
  padding: 8px 2px;

  & + .note-pairs__row {
    border-top: 1px solid rgba($lh_color_navy, 0.06);
  }
}

.note-pairs__main {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
}

.note-pairs__label {
  font-size: 12px;
  font-weight: 600;
  color: $lh_color_blue_gray;
  flex: 0 1 auto;
}

.note-pairs__label--group {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.note-pairs__value {
  font-family: $lh_font_mono;
  font-size: 13px;
  font-weight: 500;
  color: $lh_color_navy_dark;
  text-align: right;
  overflow-wrap: anywhere;
}

.note-pairs__value--free {
  font-family: $lh_font_family_dm_sans;
  font-weight: 400;
  text-align: left;
}

.note-pairs__sub {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  padding-left: 10px;
  margin-top: 4px;
}

// ── Grid table (desktop + viewer + wide grids) ───────────────────────────
.note-table-fit {
  width: max-content;
}

.note-table-fit--fit {
  width: auto;
  overflow: hidden;
}

.note-table {
  border-collapse: separate;
  border-spacing: 0;
  font-family: $lh_font_family_dm_sans;
  line-height: 1.5;
  width: max-content;
  transform-origin: 0 0;
  transition: font-size 200ms ease;

  td {
    padding: 7px 14px;
    text-align: left;
    vertical-align: top;
    white-space: pre-wrap;
    overflow-wrap: break-word;
    max-width: 340px;
    background: $lh_dash_surface;
    font-family: $lh_font_mono;
    font-size: 0.96em;
    color: $lh_color_navy;
  }

  tr:nth-child(even) td {
    background: mix($lh_sidebar_accent, #ffffff, 5%);
  }

  tr:nth-child(even) td:first-child {
    border-radius: 10px 0 0 10px;
  }

  tr:nth-child(even) td:last-child {
    border-radius: 0 10px 10px 0;
  }

  tr:first-child td {
    font-family: $lh_font_family_dm_sans;
    font-size: 0.92em;
    font-weight: 700;
    letter-spacing: 0.02em;
    color: $lh_color_navy_dark;
    border-bottom: 1px solid rgba($lh_color_navy, 0.12);
  }
}

.note-table--plain,
.note-table {
  td:first-child:not(:only-child) {
    font-family: $lh_font_family_dm_sans;
    font-weight: 600;
    color: $lh_color_navy_dark;
  }
}

.note-table td.note-table__cell--label {
  font-family: $lh_font_family_dm_sans;
  font-weight: 600;
  color: $lh_color_navy_dark;
}

.note-table td.note-table__cell--center {
  text-align: center;
}

.note-table td.note-table__cell--right {
  text-align: right;
}
</style>
